import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import classNames from 'classnames';
import {
  blackColor,
  goldColor,
  grayColor,
  hexToRgb,
} from 'assets/jss/material-dashboard-pro-react';
import { post } from 'helpers/apiHelpers';

import DATA_TYPES from '../constants/dataTypes';
import DEFAULT_MULTIPLIER from '../constants/defaultMultiplier';
import MultiplierModal from './MultiplierModal/MultiplierModal';

const blackColorRgb = hexToRgb(blackColor);

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    bottom: '0',
    padding: '22px 64px 22px 24px',
    color: grayColor[16],
    backgroundColor: goldColor,
    boxShadow: `
    1px 0 4px 0 rgba(${blackColorRgb}, 0.14),
    0 1px 4px 0 rgba(${blackColorRgb}, 0.14),
    -1px 0 4px 0 rgba(${blackColorRgb}, 0.14)
  `,
    borderBottomRightRadius: '6px',
    borderBottomLeftRadius: '6px',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '24px',
  },
  button: {
    display: 'flex',
    padding: '0',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'inherit',
  },
  buttonCheckAll: {
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'uppercase',
    textDecoration: 'underline',
  },
  checked: {
    marginBottom: '0',
    fontSize: '14px',
    fontWeight: '400',
  },
  multiplierButton: {
    padding: '5px 16px',
    fontSize: '14px',
    textTransform: 'uppercase',
    border: `1px solid ${grayColor[16]}`,
    borderRadius: '3px',
  },
});

const getMatrix = checkedData => {
  const matrix = [];

  checkedData.forEach(({ type, brandIri, dietIri, calorificIri }) => {
    if (type === DATA_TYPES.item) {
      const existingBrandIndex = matrix.findIndex(
        ({ brand }) => brand === brandIri
      );

      if (existingBrandIndex === -1) {
        matrix.push({
          brand: brandIri,
          diets: [
            {
              diet: dietIri,
              calorific: [calorificIri],
            },
          ],
        });
      } else {
        const { diets } = matrix[existingBrandIndex];

        const existingDietIndex = diets.findIndex(
          ({ diet }) => diet === dietIri
        );

        if (existingDietIndex === -1) {
          diets.push({
            diet: dietIri,
            calorific: [calorificIri],
          });
        } else {
          diets[existingDietIndex].calorific.push(calorificIri);
        }
      }
    }
  });

  return matrix;
};

const SelectionBar = ({ className, total, setData, checkedData, rowKey }) => {
  const classes = useStyles();

  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={classNames(classes.container, className)}>
        <div className={classes.wrapper}>
          <button
            className={classes.button}
            type="button"
            onClick={() => {
              setData(prev => ({
                ...prev,
                [rowKey]: prev[rowKey].map(dataItem => ({
                  ...dataItem,
                  checked: false,
                })),
              }));
            }}
          >
            <Close />
          </button>
          {checkedData.length === total ? (
            <p className={classes.checked}>
              {t('reports.checkedAllResults', { checked: total })}
            </p>
          ) : (
            <>
              <p className={classes.checked}>
                {t('reports.checked', { checked: checkedData.length })}
              </p>
              |
              <button
                className={classNames(classes.button, classes.buttonCheckAll)}
                type="button"
                onClick={() => {
                  setData(prev => ({
                    ...prev,
                    [rowKey]: prev[rowKey].map(dataItem => ({
                      ...dataItem,
                      checked: true,
                    })),
                  }));
                }}
              >
                {t('reports.checkAll', { total })}
              </button>
            </>
          )}
        </div>
        <button
          className={classNames(classes.button, classes.multiplierButton)}
          type="button"
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          {t('reports.setMultiplier')}
        </button>
      </div>
      <MultiplierModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onIndividualMultiplierSubmit={async ({ firstRange, secondRange }) => {
          const [firstDateFrom, firstDateTo] = firstRange;
          const [secondDateFrom, secondDateTo] = secondRange;

          try {
            const data = await post('/reports/multiplier_calculator', {
              firstDateFrom,
              firstDateTo,
              secondDateFrom,
              secondDateTo,
              matrix: getMatrix(checkedData),
            });

            setData(prev => ({
              ...prev,
              [rowKey]: prev[rowKey].map(dataItem => {
                if (dataItem.checked) {
                  if (dataItem.type === DATA_TYPES.shopDishesItem) {
                    return { ...dataItem, multiplier: DEFAULT_MULTIPLIER };
                  }
                  const { multiplier } = data['hydra:member'].find(
                    ({ diet, calorific }) =>
                      diet === dataItem.dietIri &&
                      calorific === dataItem.calorificIri
                  );

                  return { ...dataItem, multiplier };
                }

                return dataItem;
              }),
            }));

            closeModal();
            // eslint-disable-next-line no-empty -- FIXME
          } catch {}

          toast.success(t('reports.multipliersSaved'));
        }}
        onSharedMultiplierSubmit={multiplier => {
          setData(prev => ({
            ...prev,
            [rowKey]: prev[rowKey].map(dataItem =>
              dataItem.checked ? { ...dataItem, multiplier } : dataItem
            ),
          }));

          closeModal();

          toast.success(t('reports.multipliersSaved'));
        }}
      />
    </>
  );
};

export default SelectionBar;
